import React, { useState } from 'react';
import { usePagination, DOTS } from './usePagination';
import { Button, Flex, Text, ButtonGroup, Spacer, Select } from '@televet/kibble-ui';

interface PaginationProps {
  recordCount: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSize: (pageSize: number) => void;
  isLoading: boolean;
}

export const Pagination = ({
  recordCount,
  pageSize,
  currentPage,
  onPageChange,
  onPageSize,
  isLoading,
}: PaginationProps): JSX.Element | null => {
  const [takeFocus, setTakeFocus] = useState(false);

  const { paginationRange, totalPageCount } = usePagination({
    recordCount,
    pageSize,
    currentPage,
  });

  const handlePageChange = (page: number): void => {
    setTakeFocus(true);
    onPageChange(page);
  };

  return (
    <Flex aria-label="Pagination" align="center" onBlur={(): void => setTakeFocus(false)}>
      <Text size="sm" variant="subtle">
        Page {currentPage} of {totalPageCount}
      </Text>
      <Spacer />

      <Text>Show</Text>
      <Flex basis="auto">
        <Select
          value={pageSize.toString()}
          size="sm"
          ml={2}
          mr={3}
          backgroundColor="background.default"
          options={[
            { label: '10 Rows', value: '10' },
            { label: '15 Rows', value: '15' },
            { label: '25 Rows', value: '25' },
            { label: '50 Rows', value: '50' },
            { label: '100 Rows', value: '100' },
          ]}
          listProps={{ onSelect: (selectedItem): void => onPageSize(parseFloat(selectedItem.value || '')) }}
        />
      </Flex>

      <ButtonGroup isAttached={true}>
        <Button
          size="sm"
          variant="secondarySubtle"
          disabled={currentPage === 1}
          iconName="chevronLeft"
          onClick={(): void => handlePageChange(currentPage - 1)}
        />

        {paginationRange?.map((page: number | string, index: number) => {
          if (page === DOTS) {
            return (
              <Button key={index} size="sm" variant="secondarySubtle" disabled>
                {page}
              </Button>
            );
          }

          return (
            <Button
              key={index}
              size="sm"
              variant="secondarySubtle"
              onClick={(): void => handlePageChange(parseFloat(page.toString()))}
              ref={(element): false | void | undefined => currentPage === page && takeFocus && element?.focus()}
              isActive={currentPage === page}
              isLoading={isLoading && currentPage === page}
            >
              {page}
            </Button>
          );
        })}

        <Button
          size="sm"
          aria-label="Next"
          variant="secondarySubtle"
          disabled={currentPage === totalPageCount}
          iconName="chevronRight"
          onClick={(): void => handlePageChange(currentPage + 1)}
        />
      </ButtonGroup>
    </Flex>
  );
};
